import { pipe } from 'effect'
import * as O from 'effect/Option'
import { ASTNode } from 'publicodes'

import {
	DateField,
	DateFieldProps,
	InputSuggestions,
	InputSuggestionsRecord,
} from '@/design-system'
import {
	dateToIsoDate,
	isIsoDate,
	IsoDate,
	isPublicodesStandardDate,
	parseIsoDateString,
	publicodesDateToIsoDate,
} from '@/domaine/Date'
import { PublicodesAdapter } from '@/domaine/engine/PublicodesAdapter'
import { DottedName } from '@/domaine/publicodes/DottedName'
import { NoOp } from '@/utils/NoOp'
import { useEngine } from '@/utils/publicodes/EngineContext'

interface DateInputProps {
	id?: string
	dottedName: DottedName
	value?: IsoDate
	onChange?: (value: IsoDate) => void
	onSubmit?: (source?: string) => void
	suggestions?: InputSuggestionsRecord<IsoDate | ASTNode>
	errorMessage?: string
	validation?: DateFieldProps['validation']

	title?: string
	type: DateFieldProps['type']

	aria?: {
		labelledby?: string
		describedby?: string
	}
}

export const DateInput = ({
	id,
	suggestions = {},
	onChange = NoOp,
	title,
	onSubmit,
	value,
	type,
	errorMessage,
	validation,
	aria,
}: DateInputProps) => {
	const engine = useEngine()

	const handleDateChange = (value?: Date) => {
		if (value) {
			onChange(value && dateToIsoDate(value))
		}
	}

	const handleSuggestion = (value?: IsoDate) => {
		if (value) {
			onChange(value)
		}
	}

	return (
		<div className="step input">
			{suggestions && (
				<InputSuggestions
					suggestions={suggestions}
					onFirstClick={(valeur) => {
						if (isIsoDate(valeur)) {
							return handleSuggestion(valeur)
						}
						if (isPublicodesStandardDate(valeur)) {
							return handleSuggestion(publicodesDateToIsoDate(valeur))
						}

						const dateÉvaluée = pipe(
							engine.evaluate(valeur),
							PublicodesAdapter.decode,
							O.getOrUndefined
						) as IsoDate | undefined

						handleSuggestion(dateÉvaluée)
					}}
					onSecondClick={() => {
						onSubmit?.('suggestion')
					}}
				/>
			)}
			<DateField
				id={id}
				aria-labelledby={aria?.labelledby}
				aria-describedby={aria?.describedby}
				defaultSelected={
					value === undefined ? undefined : parseIsoDateString(value)
				}
				onChange={handleDateChange}
				label={title}
				type={type}
				errorMessage={errorMessage}
				validation={validation}
			/>
		</div>
	)
}
